// @ts-nocheck

import { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import useStoreTimToa from "../../../store/useStoreTimToa";
import { Icons } from "../../spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { TechnicianTreeMapData } from "./types";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "../../ui/pagination";

const getLevelOption = () => {
  return [
    {
      itemStyle: {
        borderColor: "transparent",
        borderWidth: 0,
        gapWidth: 0,
      },
      upperLabel: {
        show: false,
      },
    },
    {
      itemStyle: {
        borderColor: "#555",
        borderWidth: 5,
        gapWidth: 1,
      },
      emphasis: {
        itemStyle: {
          borderColor: "#ddd",
        },
      },
    },
    {
      colorSaturation: [0.35, 0.5],
      itemStyle: {
        borderWidth: 5,
        gapWidth: 1,
        borderColorSaturation: 0.6,
      },
    },
  ];
};

export function CurrentEmployeesWorking() {
  const [options, setOptions] = useState({});
  const {
    totalEmployees,
    currentEmployeesWorkingData,
    loadingChartCurrentEmployeesWorking,
  } = useStoreTimToa((state) => state);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedData, setSelectedData] = useState<TechnicianTreeMapData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const updateOptions = () => {
    const hastotalEmployees = totalEmployees > 0;
    const hasData =
      currentEmployeesWorkingData && currentEmployeesWorkingData.length > 0;

    const option = {
      mouseWheel: {
        zoomSensitivity: 0,
      },
      legend: {
        top: "2%",
        left: "right",
        orient: "horizontal",
        textStyle: {
          color: "#fff",
        },
        data: ["Técnico com atividades", "Técnico sem atividade"],
      },
      title: {
        text: hastotalEmployees
          ? `Total de Técnicos: ${totalEmployees}`
          : "Carregando...",
        left: "center",
        top: "3%",
        textStyle: {
          color: "#fff",
          fontSize: 14,
        },
      },
      tooltip: {
        formatter: function (info: any) {
          const value = info.value;

          if (!hastotalEmployees || !hasData) {
            return `${info.name}<br/>Quantidade: ${value}`;
          }

          const percent = ((value / totalEmployees) * 100).toFixed(2);
          return `${info.name}<br/>Quantidade: ${value}<br/>Percentual: ${percent}%`;
        },
      },
      series: [
        {
          type: "treemap",
          visibleMin: 300,
          data: currentEmployeesWorkingData,
          leafDepth: 1,
          zoomable: false, // Adicionando esta linha para desabilitar o zoom
          breadcrumb: { show: false },
          label: {
            show: true,
            formatter: function (params: any) {
              return `${params.name}\n${params.value}`;
            },
            fontSize: 12,
            color: "#fff",
          },
          upperLabel: {
            show: true,
            height: 30,
          },
          itemStyle: {
            borderColor: "#fff",
          },
          levels: getLevelOption(),
        },
      ],
    };

    setOptions(option);
  };

  const onChartClick = (params: any) => {
    if (params.data) {
      const category = currentEmployeesWorkingData.find(
        (item) => item.name === params.data.name
      );
      if (category) {
        setSelectedData(category);
        setModalOpen(true);
      }
    }
  };

  useEffect(() => {
    if (totalEmployees > 0 && currentEmployeesWorkingData.length > 0) {
      updateOptions();
    }
  }, [totalEmployees, currentEmployeesWorkingData]);

  const getCurrentPageItems = (items: TechnicianTreeMapData["technicians"]) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return items.slice(startIndex, startIndex + itemsPerPage);
  };

  // Função para calcular o total de páginas
  const getTotalPages = (totalItems: number) => Math.ceil(totalItems / itemsPerPage);

  // Reset página quando muda os dados
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedData]);

  return (
    <>
      {totalEmployees > 0 && currentEmployeesWorkingData.length > 0 ? (
        <div className="bg-gray-500 shadow-md rounded-sm">
          <ReactECharts
            option={options}
            style={{ height: "450px", width: "100%" }}
            onEvents={{ click: onChartClick }}
            // opts={{
            //   renderer: "canvas",
            //   height: 450,
            //   width: 600,
            // }}
          />
        </div>
      ) : loadingChartCurrentEmployeesWorking ? (
        <div className="h-[450px] bg-gray-500 flex justify-center items-center rounded-sm">
          <Icons.spinner className="h-12 w-12 animate-spin text-orange-300" />
        </div>
      ) : (
        <div className="h-[450px] bg-gray-500 flex justify-center items-center rounded-sm">
          <p className="bg-slate-100 p-2 text-gray-500 rounded-md font-extrabold">
            SEM DADOS
          </p>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-[1400px]">
          <DialogHeader>
            <DialogTitle>{selectedData?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4">
              <p>Quantidade: {selectedData?.value}</p>
              <p>
                Percentual:{" "}
                {totalEmployees > 0
                  ? (((selectedData?.value || 0) / totalEmployees) * 100).toFixed(2)
                  : 0}
                %
              </p>
            </div>

            {selectedData?.technicians && selectedData.technicians.length > 0 ? (
              <div className="space-y-4">
                <div className="max-h-[400px] max-w-[1400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24 p-0">Matrícula</TableHead>
                        <TableHead className="w-20 p-0">Filial</TableHead>
                        <TableHead className="p-0">Nome</TableHead>
                        <TableHead className="w-32 p-0">Matrícula TIM</TableHead>
                        <TableHead className="w-24 p-0">Centro de Custo</TableHead>
                        <TableHead className="w-56 p-0">Função</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getCurrentPageItems(selectedData.technicians).map(
                        (technician) => (
                          <TableRow className="h-12" key={technician.id}>
                            <TableCell className="p-1">{technician.matricula}</TableCell>
                            <TableCell className="p-1">{technician.filial}</TableCell>
                            <TableCell className="p-1">{technician.nome}</TableCell>
                            <TableCell className="p-1">
                              {technician.matricula_tim}
                            </TableCell>
                            <TableCell className="p-1">
                              {technician.centro_de_custo}
                            </TableCell>
                            <TableCell className="p-1">{technician.funcao}</TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </div>

                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        size="default"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        className={
                          currentPage === 1
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      />
                    </PaginationItem>

                    {/* Primeira página */}
                    <PaginationItem>
                      <PaginationLink
                        size="default"
                        onClick={() => setCurrentPage(1)}
                        isActive={currentPage === 1}
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>

                    {/* Ellipsis esquerdo */}
                    {currentPage > 3 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}

                    {/* Páginas ao redor da página atual */}
                    {Array.from(
                      { length: getTotalPages(selectedData.technicians.length) },
                      (_, index) => {
                        const pageNumber = index + 1;
                        // Mostrar apenas páginas próximas à página atual
                        if (
                          pageNumber !== 1 &&
                          pageNumber !==
                            getTotalPages(selectedData.technicians.length) &&
                          pageNumber >= currentPage - 1 &&
                          pageNumber <= currentPage + 1
                        ) {
                          return (
                            <PaginationItem key={pageNumber}>
                              <PaginationLink
                                size="default"
                                onClick={() => setCurrentPage(pageNumber)}
                                isActive={currentPage === pageNumber}
                              >
                                {pageNumber}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        }
                        return null;
                      }
                    )}

                    {/* Ellipsis direito */}
                    {currentPage <
                      getTotalPages(selectedData.technicians.length) - 2 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}

                    {/* Última página */}
                    {getTotalPages(selectedData.technicians.length) > 1 && (
                      <PaginationItem>
                        <PaginationLink
                          size="default"
                          onClick={() =>
                            setCurrentPage(
                              getTotalPages(selectedData.technicians.length)
                            )
                          }
                          isActive={
                            currentPage ===
                            getTotalPages(selectedData.technicians.length)
                          }
                        >
                          {getTotalPages(selectedData.technicians.length)}
                        </PaginationLink>
                      </PaginationItem>
                    )}

                    <PaginationItem>
                      <PaginationNext
                        size="default"
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(
                              prev + 1,
                              getTotalPages(selectedData.technicians.length)
                            )
                          )
                        }
                        className={
                          currentPage === getTotalPages(selectedData.technicians.length)
                            ? "pointer-events-none opacity-50"
                            : "cursor-pointer"
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            ) : (
              <p className="text-center text-gray-500">Nenhum técnico encontrado</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
